"use client";

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar } from "lucide-react";
import { toast } from "react-hot-toast";
import moment from "moment-timezone";
import {
  getDefaultDateRangeThisMonth,
  formatCstDate,
  formatCstDateRangeLabel,
  getCstNow,
  isDateTimeInBusinessRange
} from "../utils/cstDate";
import { useAuth } from "../context/AuthContext";
import { positionTipoutService } from "../apis/positionTipoutService";
import { employeeManagementService } from "../apis/employeeManagement";
import DateRangePicker from "../components/ui/date-range-picker";
import { InlineTipAddBar } from "../components/inline-tip-add-bar";
import { PositionTipEditModal } from "../components/position-tip-edit-modal";
import { PositionTipDeleteModal } from "../components/position-tip-delete-modal";
import { PositionTipsTransactionTable } from "../components/position-tips-transaction-table";
import { PositionEmployeeTotalsTable } from "../components/position-employee-totals-table";
import { TipEditLogsPanel } from "../components/tip-edit-logs-panel";
import { isActivityLogViewerRole, isAdminRole } from "../constants/roles";
import { positionSupportsOweToToast } from "../utils/employeeRoleUtils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const base64Logo =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANUAAABvCAMAAABMz3WiAAABfVBMVEVHcEzBwL45LyX59/X7+vhxXEb38+/9/PvNvKl6Ykk/Lht6X0Pax7P49O+tlXxVPSTayLSOc1f28eqzmn9gQyeafV/27+VxUzXIsJVWOB1wUzTr3s+rjGvBo4R4WTjMsZKVdlTdyK1nRyi/nnurimjq2b+AYD7WuZW6mHKXdlF0UjHDoXmvjGXZvpyMa0ZqSil3VzTkzapUNBmmhFzDnnSUdE2FZD9bOx1xUC7Mq4Tiy6PWuJC8mnRiQCCCXzp0US6lglzTsYbIpnyuimSZdE2/m3Hew5aQbUZyTixkQSKGZD99Wjavil6feE+YcUmlf1SJYz7StIPIp3a/oG+5mmq1lWWxkGGujV6riVuohVikgVWhfVKdeU6adkyXc0mUcEeRbUSPakKMZ0CjXDyKZT6JYz2HYjyFYDqDXjiBXDeRUDN+WTR7VjF4Ui91UC2aOiZyTSpuSSeIOyazJxtpRCSoHhVjPyCKKxyfHxafGBGYFxCTFhCOFA+HFA+AEQ1g9lSzAAAAVHRSTlMAAQMEBwkLEREQExkbHCAiJyYqLzE9QUBDR0pPUlVcYWJmaG1wdXV4hYiIlp6hoaCipaS1tri9vb7DxsfHz9HQ2Nva2tvd3uDh5Obn7Ozu8fD5+/4HuOOJAAAWkklEQVR42uzX3W9c13WG8edda5+h5KR1YicxHBtIAsRJW/Tjqr1q7/r/XxRIA6RB7Lb+0gcl0aI0M+fstd4ahGRRtkzY7hQICv+IwSE5wGCes/Zs7OF73/ve9773ve/9vyK4mcT/pcF1cXvwHWyHBkC3dnwz01vZvMLyGi883U5wV3Xrlz8URoA7ZAEWWARXOuBKEzz34L8PBuCtdxYQgHlB5iWCvT7bP31azXUvXuKZ7aO7/t/PyvHaL3dNh0x3pLVMdao8nCO97liRijNmbLkcI0Ol7d+SKxFvvefNGmFZSVXSke7Ewm2RbBNGke+ff3b+eG++JH729zzTf3yQ8wQrELw2YecGSNOW1VPB1uoqmbnztKKaaq0he9OIAjCuue7qeAZzZHumHJjpmdDYLPOoqIJ3ftn/fu/uxTQvkVZAZbH2CL6T4GXZ4PYE0dCRYSkxbJYbBbtwiMCD7rCSF4rUULV7ThiaTfVsJZOs8nFVBkF7zv17//zrt2/zMtO0QLZtOMms2gqaIUqhgRDKMCgdPbLtRtmjSOYSJfEFpUtTMbWcBRDduAdCJdw9ghpFp9uhqX948/2PPjMvCSh35lwDTjQrNUaepcC0rQjVdCojU62kXJ0iApxahnkhVE2x0CVByy6leoa2KivmNLVWRpYX1bu/euc1XtbHCKAYrRNVhUIBTbs7ol12R2S6Wqg22xUKNrq2pkf35BlDZCDl+FwRXdvWS1fPVm/TSE0pFBoDRUpv/+YXP+BlKUnAwCeqagVtG2EHUUjtOFOrq3j2ZqJhGWjnOdU8J1gPweahhpBnnu1cEEPTA2pfy6gWIuLsNZzoJ795Z+ElcqGQ+mQrEGyFZA2prBySgnYQChxackjEQGlH5m4Rzxg2N2P01j27N2VET2+blpSxmat2QkjytLD7x7/+aXBNqNsGjT5RVZBJK8lBqmwkBa6QHZ41C4VZwpZEaFy7pQLlFtQmOr15uBTabzWRZ+UiqRUkLR+qY1a33n33da4pxxBVXSc6MdFTttsWW7bDlFCHy6ixPMlJF8Xcna0Vc3doXhil9nCnfni4deDWgd3nD+BW3gY29jN7U3Y7hcSuwj9/dNF8QV0ZpqNDp6lSAVjacMNRSJ3QQoSYYY9pKkxsbtGbt4Uv2JFbQJzFLl6bsevPL2uMGTsGmGXbPICK0RVRRcwfvf76Q67pztbotk9RpX58BwAZZGRBB010QBM0ENBAFgkWdRlcMcwzY8fkTIhBMxfoOQDmkMdYHo+ZS3WKomEw3r73yDwXq1CxkOYUVd7uPeQ7mE8aAMGtpG6PufhwazFMYBvBiOYqrYIf8Njbrg3qzKnOeuMvb+15rrOD7KbjJFXUhQjouPqB61cgnj/x3PM/yjwzylZlzR3ILJ6kGWqNORTVO3Gbue+sjqiZi5vSzz46mGdaSERjTlPl4hVkAJlrBPCV/xfVgdsFXQPCi9rM4WCxewZe+eH+OHdYHs4wlb59O4pn1DnaeJyg6gYGwFznV/wG6T23s30ccwdzgRZzoWDtnewBRLIs3VHkgrs1evvRD7P9RZWdYoT4roKTqqruWFSGZQkaM6xUgCNDSEsyNLfOiTyPigwYO75Q+4kN9J9FlSoJmBP3sQgbTQUhNM7CtgXCLAo3nuWka3PfgiuCSMk1wSfYLU7AEDqc5cGRtAuInStKOKIhZLA32FFelkkWteWyMrhi6HGmtmH8mazABDO3ahoQ0BCEts2W5GoDu+UWG9k1sLVkhXjBHtmk232aWUl8AzZfT1GbmCYUDYAMAxrUCpugyUl2KTqIkMU1rhGdsnSaqvH6DrD4ghGAZQEWsF0ezCupcNuoibNdWKyLkbBrBPZc7MQIRskZdPS2UN4XX5j2HOD2aap2P3+XShnhojJVJEbPH2a99f62Fq/kxIpNRhBYQBhLGYI4Nt56kaNK7oiJGqri8Vx5Ljs1gHmy3eLsTXdkkdjYZOZcjnbSGRNax22t5tUMBJiwaEV7CdsBsjBLh7YthYkac4fcNI6tVswV0TGEPXYrJ6oSZVWrygoT2ROm8cBzqtTzg4ujeTWBWzK4oCVVD5vuAKk16B1y+EDvMKbVEX18+sQ84wpL6oJTVXlzS21ROVuLQdMCoE1yvLzcb3wNg4yAKOSW5BmNKhIRBgLs2Gi7I7B36m29NwuuWEEpWtbJzhZtq9buwqR6a1LOJai20pTvnT81X0MgOxEhrNgcMigW2W6wQnawbggqmA5ge3R40jyXENic7nQbatnARKDhzsiwaSGY2527T4obWCqghawBEtlbCGxLtix4nAkp20OdXZcXZZ7blJbsk1UJbJYKVdsGFyGXw4q+GtV+cpMoWgDHOQJjezGhLSINRoankznWDJO0an9+//rGLqBUA9dJVqCMgojMbkohV1fTDqWQZn9wcVl8LSPabRAAIctJ7GQaG9sOrXu7qa26sf1k/+nF3nwhRKPUiDzJrCySFpot0UgtV9qSJu66vLzY+HrCiMCyqNoJ9ZaNxWgDEvKTJ1eDS5Pt9OHw8YOL4gVPsLILdIpZYaaXQSMJHEKiIy1GePre+TRfz6DIgZRNYjvizGBEhCTE8fyxBsrKkSlxfHr/7mfTvKAgkxTgk1QJlz1JFOme7QlVeKu1uu7evFUgRKBIOs7yLEUra87Hx+MWq9bjk8vzB+tcSbkctDg8vffh+b64xh0KiuZEp1vI6SGDRm9NqUth2RB1+flN5WYuJFkYQJinj2OLFS+6ZLqHgppLmYroerrd//D8q69qB12hU1UpEUT2tO/smTwzJhwujsXNJLusVCMQclVExhxsHfRwHAhs5NZhPd65+5UohWR3A6eqIjuMlcC9c65sy7PrceNGxuGCDoPBYbPlGF6YuBW5xdkKnRXb2tuDh3c/e1q8LOyUSXy6qjI2ok1cbuY6mRsJ2pJMB1SEYYugYpStaM1w79q4+3g+H12eP1nNlxQTQtHrifZAKNONjPgqczOjsEZKEShCopbd4TBpz17CXcy5ueYM7z/5w3/+11dOygLBJFORcKKqwJvaKNp8W8KEbEyhVF8leowu5wBmr1BImbs33/jBcSu+xBAolVjgE1W1JYtZ5tszNGHkEI0lhM7ORlDREwo6czpnKX773ns/XfgqabSnR83TzSqS7kIO8W0JyVWGPEOWbSBLAUk1gu4lmNCht//pt2+/Jr7MBknFOFmVAiOHim/P5W7cMTRvhbANni6kjWC5vVsUDtop0f3Xf/fLN4PrAkbmwEme8PtVNiMB2XxrpQjJhX1ACgkI5qwcElUSdayOsRW5DP3qX3/91m3xQkMMQGCj0+zsUUABpvn2tvO3wOBpLAQCSZigS3YZNhYCQxX5j2ef3HncXLMZtDA7Tve5sgJjvpP1AC2kAEAILEUk3eHCZc87H2p29lS4mX/z3q/eWLgmRUa34mRniwZjEQQG8+3Mp1nOEEQCyM0IKlF5aubWMJ9+2r/G1IApePvN5U8PzHXyceTRuE9SxewQQhSaY/CN+Nhc2S4/+tmSG2nWdSdL82ii28SeQYmo+uxhvvFTyIlC3o6Hg6N4wUIjS47a7fhmtumvrXIsqwMTHaE3fhBNYGQBFgaEQWBhBFiPPj2aKxf336Jxo+3BXyywHdasYy9CGcfOaNX54cnxtbebyiBmHY7v33nSXNPRkNjvfvIG30jp0cfr11aJ3M0O5Hb6VxkoMxKo9m6DaDxkMecI7WY75vLk9/ePcGX/6OGPOyxhP7jlY/Q4duYoamJ1p9f1otZPfvdXbSqp4/E/Hlys5poQrYLgX6IJrqydJJUUSUAHTS1sGc2T3z/UTZ+rDdlZGBMuYWtNS/Qed4C3lEU5iH2nel01orjS9z7+2WoNPFFvRIaKIdWKVAof5919+cGnb/xEY1u1Ht9/cH81LwuCEnUUBKCctQ0qqgwVlVRqcxQx6fXR+b5uqApNFOUgZYHkltO9a4wisOwOdw77YJUD8cLF/buvSwGQGqJDhaq7CKTu452Lo9k+rw836+FPH31pUiBXdqYFBMhQkNFttRRVAuGgPbrDl3cv93XzOdC4DWHbBBJqYm6OyAFuKwQKU5LsCF7oO38KLb3RyBGN7XArb0d1uA6P7+034NGnH9Fe93+4+5WoMiKccoYBhK1cYCgEJQPQRqMJP1nvXTz1DVUNciPLchUyxEAqYbrd0KaK2spqEATXHD75Xa/O5mwng45eFi2pXFLute6d7w343vua6/5Pr1h+FNl0AQ2N0RAQEIiQMXSToUni48MHT4qbVuCw2sKJ5JAVoU5aSKaVtNxGYTeYQM01dfHxX75bO+aUjaZjUKZbyza3+cFHlxOA9e7vfnF4/+4roqzOhIoxozsYEFQp1dEB7qBRdIehfDh+up/c/K0RhyLSrYgQWEG6Q9AFWqJt4xGhCJPR5rrt/h8/9LZRHaOmbuHycTWadVw/uHuxPa//9OEfXhWFiLQjoMiEgiYzUlZAEWCD3VDlw0cPLoubzxZyY1qJF2PUUmFnNw41S4crbBlHEEZc5+2TefG3oCCkQZMrjvZ2uP/oWsa8q4cXr4jCzQRZJCw9XZCIAlNmANWJiGl0ePTgs40bq8IGQBltJCur1R1Lj2kZz2EHFY1Cahc2LynuzX7nJzWCcKBRUm9zrucf/k/71tYbx22Fv0MOd4d7mb2Odi2tbmtVlixBMWLXdYAkDRAXcXNpn/oU9LHoS/9p3logDwXaum5sK7K0uux9Zkh2xJmRR7aTuAUCJMB+i+Wcc3hIno8cYokzs+d5GpPHJjRvPvgTVyAOOCq8LMAj4hG4IUPEoECUbDKmEYwPT+fmhzJnBjCabBYaAFFElhvXYMqQ0UaR1kYTgQHGbrHXwsJgdl5baRAYKCSDSKkoOjmenYyC/Jp+xznTQPHIBkbKADpgUFZhiOyNRzZOZQgEzI5PpuEPvvWoWRIopbtOM2ZPBsYYnbw4qGORaSIeErM2JV6lpUdqNC5Xq3UTgM21DtXZi9n5eaTeJrcThA4zhl8WiGzeEIYcCpnRmpQRBKXIRsmg9cw+evj+/4n0l0QIAYRWTR9xJ6eUy0IBsZKeCq80c/bvc4Xr4CRFVXLpSmCE0WwyHb9dHqKzXuJWUM5Lf67SL2BjyWFy+GyO72UFx5P4PxC+adcTcwrCSaYoCudK4a3g1vn/NvZ0on6AFSPKazot8pbXjd/9ZoTdWIDRMHhb8FxIb5VhVQY/Agg/EljyWfxHb4EFFlhggQUW+EmARLlWllLGJWPyUmYAYEXBSrFeTr4s5wgAolSrSVmSDAlkuVEui6wmlkiU4kuiXTariayNyNoQi0HItFgVjOhaRXzJFGumK8/ESiwD5Vl5a/d3u61Wb3ffE43b93fqDgBcilt1ub2/2uo/2G31Dna98qXQisUDz7Jov3u/1/LX+jUGgLU3eq3G8loNILn87oONClF5ebfnkuy8+6DXavnb7yyTvPGrO37L7/dLydildr/vexwWTNb8VqPdLjFA1Nb6aw0BsNpyR5ZipWyjaq/5LoHJdqfRaHfaNYFSp99vx8pav81zvNjal/fqjuN29+oQt/+4V7R1JO98uVeQu7eq4vaf9ytub79C/T/dvXT091vWo/bod6uO23jnQQdgnfs3K5zL7Ts1AO0PPutxQPrdIk80xyl4uxucbvz2k5bjrn1wp5yM3fn1p1tFnsil/m634pbXtmssVt75y0G9QCC50SuK/qNH2wIAarvdIrHStvVcvrPrkeg8+rRXcb3Owa5LL3NMBiNEUTQ7uciyiokVUBqjb4cKIeLaGQyMdRydpe2mJq44G9Y7EtVNdTpSajpwV2RSqSBcPZgrABBQUXBxHNle4zbH4YpvI9DKvrBqg3G77SAIwtlJaVPChOFEK22A+Tgy0fMnN7s+AzAZzgy53bVJ7Dk/PuOMRYpzCqPJ6SB03pA5iyYRAIUr0KVthgTzc2ux4nSOlzBTfqPEWt3xCADG0w0vbcsqbBggg6172eaGy/PvWtplWFXDcaD0dLKywoyNzuZR9Eyb6OSrvV41+XOMij1Pp+NQqXA4TXmYKJ61GV5jRYLGAQCNPPQ4zAIZRoADgAnEjjloVRDVJR0qyzhca4qk8yq75idYMNbptBFmhl1PYJDb+MUsigCjR6hLQgbS2gDBN1/H+ytJrrhLl54GxozHSG4pEGPhNLiejRGAUz+bkR0wD6VNytTMAXAGFMvnc+TB+CQQdQTaajOnIkPAoLl0NINFCMCwUjRWBinh2uNXkx0kvJkKrRgMu89mQJhPlWP0vLI1mmsokJBh6hkOslUgiZnS19ZKh065U+Mw11lRNpXGpIWJOflNbvUMosEOQ455YtQgCADkLD1ckqkHgHJzpQTbDVij2xr8a/AKKyPqF2E6M9RwOcCzKCyCF0/XerXU89R6EjNRZAAEBm6rhFCZPCsmXDdZOIUczGuC1oIzneNtZNmXj58P863Klokz+vumT0jhcjJXMmORHgW5kK9LCAGHru4knV4unn+93JVQV3ThVH2/KggoFqvVepHDIM8K4WhwfAYQeH4QzV8fcTQ8HOUMxVKJHz0+z2e0zDDZYEdP+sseUszGgyNNZGOcnB0OvM1yNl8cqTRFCoHIWHIWLCsHh2Hf5/mpF5vrVW4ARMH5afSGpwdmfhYJB8j4ugSQepWVBkx4FgonswTD82+fDRXC0wLShtE8sjfF7OlXd1ck0vC0Gg7JTQZU0Xi60YBFFiNzwnk6OAHngSVnYdLHPjp68c1GtwaFcOJZTxUWvEgDmOggGs60k01AnlZUkLk14LlqgxzUTFckMgRBoOzPSJgw5eJ5GMDi4tk/NhsEQLDEs+gyZPBdTshBloKLViE7ws0DAKyYkGScpR0e/u3OUgGYHPNCGlaUNIZRoxGVeP5XOLuUBACe3vvXyBDyoEIRryA8GnquPR/IwYsQFub4SX/NAxBqWLACUkRgDncIGWLVjU6elZgBwKU4nCo1+w9jOhva4QZGDw6PNnyO8OTQehLIuQpPRY6b/xUmDQu3LTICTNbYFSuOFJnFrSVCnvjgSdEDwGvh01FmnBz99ZcrMlsrsFqNIRFV6BAvJDKHBvGypPMn1SpArFA5GYVKXwybdrGKLADIBhE9/Wez6jBcJJ4QFaTQBqxQNAwZSG795r26LDc23u/y8u7ne54s+3d33MbB57cqBBLWJggkt7+414odt++uAiDRvv/xqkvZftrYashyZ2e9CIjOhx+tCiJ5+9OHGzLWvujJcnn5YKcglj/5uCuA5Q8P7KGWOg//cKsgZPtgq+jUd3YaQpQ29jebDkhsfLTfkKLc8grEatvWm/sf3WtycOvJStsP318psM5nv1+Pu6htH+Ty2eR2NzfrntdaXa9yr7u5Uvda3fWu661urlQ4yLU2l8C87vp64li3SxYL3YwVUKi3YtQLAGRSQ9JfX+96VvNsp45sxV24BLd3s+sCkP7ee/HgfsykyAv11V7D83srzQIAp76+t9Xz4x45hL9lRyL35q0mB8Weqy3P39qM+Ut//95mq9HY2N1p5lgR59xJvsRTZCIBVwKQOGUarIYMjLjjpKr1Sfyda51SbLGtecFuK6fSXGpWXLferBZATrFScSvVogMg1qrNer1SKXCAu/VqgRJbkQCeelarRS4qzeZlF/GlSD+NdDqIkwUnAkAWuUqe2JGZr2opQepFoMuSFjmABRZYYIEFfjb4L2/bnLmPQWqLAAAAAElFTkSuQmCC";

export default function PositionTipsRecords({ position = "Bartender" }) {
  const { currentUser } = useAuth();
  const canViewEditLogs = isActivityLogViewerRole(String(currentUser?.role || "").trim());
  const canClearEditLogs = isAdminRole(String(currentUser?.role || "").trim());
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);

  // Edit logs state
  const [editLogs, setEditLogs] = useState([]);
  const [editLogsLoading, setEditLogsLoading] = useState(false);
  const [editLogsClearing, setEditLogsClearing] = useState(false);

  // Modal states
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  // Filter states
  const [dateRange, setDateRange] = useState(() => {
    const { start, end } = getDefaultDateRangeThisMonth();
    return { start, end };
  });

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  // Balance data state
  const [, setBalanceData] = useState({
    tipPool: 0,
    collectedFees: 0,
    addedTokes: 0
  });

  const positionTitle = useMemo(() => {
    const p = String(position).trim();
    const lower = p.toLowerCase();
    if (lower === "janitor" || lower === "custodian" || lower.includes("janitor") || lower.includes("custodian")) {
      return "Custodian/Janitor";
    }
    if (lower === "waitress" || lower === "server" || lower.includes("waitress") || lower.includes("server")) {
      return "Server/waitress";
    }
    return p.charAt(0).toUpperCase() + p.slice(1);
  }, [position]);

  // Fetch employees once
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await employeeManagementService.getAllEmployees({
          limit: 1000000
        });
        if (response.success && response?.data?.body?.data?.employees) {
          setAllEmployees(response.data.body.data.employees);
        }
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch balance data
  const fetchBalanceData = useCallback(
    async (startDate = null, endDate = null) => {
      try {
        const params = {};
        if (startDate && endDate) {
          params.startDate = formatCstDate(startDate);
          params.endDate = formatCstDate(endDate);
        }

        const response = await positionTipoutService.getBalances(
          position,
          params
        );
        if (response.success) {
          setBalanceData({
            tipPool: response.data?.tipPool || 0,
            collectedFees: response.data?.collectedFees || response.data?.paidOut || 0,
            addedTokes: response.data?.addedTokes || 0
          });
        }
      } catch (err) {
        console.error("Error fetching balance data:", err);
      }
    },
    [position]
  );

  // Fetch records
  const fetchRecords = useCallback(
    async (startDate = null, endDate = null, showLoader = false) => {
      try {
        if (showLoader) setIsLoading(true);
        const params = {};
        if (startDate && endDate) {
          params.startDate = formatCstDate(startDate);
          params.endDate = formatCstDate(endDate);
        }

        const response = await positionTipoutService.getTipHistory(
          position,
          params
        );
        if (response.success && Array.isArray(response.data)) {
          setRecords(response.data);
        } else {
          setRecords([]);
        }
      } catch (err) {
        console.error("Error fetching records:", err);
        setRecords([]);
      } finally {
        if (showLoader) setIsLoading(false);
      }
    },
    [position]
  );

  const fetchEditLogs = useCallback(async () => {
    if (!canViewEditLogs) return;
    try {
      setEditLogsLoading(true);
      const res = await positionTipoutService.getEditLogs(position, {
        startDate: dateRange.start?.toISOString?.() || dateRange.start,
        endDate: dateRange.end?.toISOString?.() || dateRange.end,
        limit: 200,
      });
      if (res.success && Array.isArray(res.data)) {
        setEditLogs(res.data);
      } else {
        setEditLogs([]);
      }
    } catch (e) {
      console.error("Error fetching position edit logs:", e);
      setEditLogs([]);
    } finally {
      setEditLogsLoading(false);
    }
  }, [canViewEditLogs, position, dateRange.start, dateRange.end]);

  const handleClearEditLogs = async () => {
    try {
      setEditLogsClearing(true);
      const res = await positionTipoutService.clearEditLogs(position);
      if (res.success) {
        toast.success(`${positionTitle} tip activity logs cleared successfully`);
        setEditLogs([]);
      } else {
        toast.error(res.error || "Failed to clear logs");
      }
    } catch (e) {
      toast.error("Failed to clear logs");
    } finally {
      setEditLogsClearing(false);
    }
  };

  const isFirstMount = useRef(true);

  useEffect(() => {
    const isInitial = isFirstMount.current;
    if (isFirstMount.current) {
      isFirstMount.current = false;
    }
    fetchBalanceData(dateRange.start, dateRange.end);
    fetchRecords(dateRange.start, dateRange.end, isInitial);
    fetchEditLogs();
  }, [dateRange, position, fetchBalanceData, fetchRecords, fetchEditLogs, refreshFlag]);

  // Reset filter inputs and pagination when switching between tip tabs
  useEffect(() => {
    setSearchTerm("");
    setCurrentPage(1);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setCurrentRecord(null);
  }, [position]);

  // Add record
  const handleAddRecord = async (data) => {
    try {
      const response = await positionTipoutService.addRecord(position, {
        ...data,
        added_by: currentUser?.username || "Staff"
      });

      if (response.success) {
        toast.success(`${positionTitle} tip record added successfully`);
        setRefreshFlag((prev) => !prev);
        return true;
      } else {
        toast.error(response.error || "Failed to add record");
        return false;
      }
    } catch (err) {
      console.error("Error adding record:", err);
      toast.error("An error occurred while adding record");
      return false;
    }
  };

  // Update record
  const handleUpdateRecord = async (id, data) => {
    try {
      setIsLoading(true);
      const response = await positionTipoutService.updateRecord(position, id, {
        ...data,
        date: data.date,
        added_by: currentUser?.username || "Staff"
      });

      if (response.success) {
        toast.success(`${positionTitle} tip record updated successfully`);
        setRefreshFlag((prev) => !prev);
        setShowEditModal(false);
      } else {
        toast.error(response.error || "Failed to update record");
      }
    } catch (err) {
      console.error("Error updating record:", err);
      toast.error("An error occurred while updating record");
    } finally {
      setIsLoading(false);
    }
  };

  // Delete record
  const handleDeleteRecord = async (id) => {
    try {
      setIsLoading(true);
      const response = await positionTipoutService.deleteRecord(position, id);
      if (response.success) {
        toast.success(`${positionTitle} tip record deleted successfully`);
        setRefreshFlag((prev) => !prev);
        setShowDeleteModal(false);
        return true;
      } else {
        toast.error(response.error || "Failed to delete record");
        return false;
      }
    } catch (err) {
      console.error("Error deleting record:", err);
      toast.error("An error occurred while deleting record");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Filter records
  const filteredRecords = useMemo(() => {
    if (!Array.isArray(records)) return [];

    return records.filter((record) => {
      const inDateRange = isDateTimeInBusinessRange(
        record.date,
        dateRange.start,
        dateRange.end
      );

      const matchesSearch =
        !searchTerm ||
        (record.employee &&
          record.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.added_by &&
          record.added_by.toLowerCase().includes(searchTerm.toLowerCase()));

      return inDateRange && matchesSearch;
    });
  }, [records, dateRange, searchTerm]);

  // Summary values
  const summaryValues = useMemo(() => {
    if (!Array.isArray(filteredRecords)) {
      return { tipPool: 0, collectedFees: 0, addedTokes: 0 };
    }

    return filteredRecords.reduce(
      (acc, record) => {
        const tipCash = parseFloat(record.tip_out_cash || 0);
        const commission = parseFloat(record.tax || 0); // 0 tax
        return {
          tipPool: acc.tipPool + tipCash,
          collectedFees: acc.collectedFees + (tipCash - commission),
          addedTokes: acc.addedTokes + commission
        };
      },
      { tipPool: 0, collectedFees: 0, addedTokes: 0 }
    );
  }, [filteredRecords]);

  // Employee totals for export
  const employeeTotals = useMemo(() => {
    if (!Array.isArray(filteredRecords)) return [];

    const totalsMap = {};
    filteredRecords.forEach((record) => {
      const emp = record.employee;
      if (!totalsMap[emp]) {
        totalsMap[emp] = {
          employee: emp,
          tipsReceived: 0,
          tax: 0
        };
      }
      totalsMap[emp].tipsReceived += parseFloat(record.tip_out_cash || 0);
      totalsMap[emp].tax += parseFloat(record.tax || 0);
    });

    return Object.values(totalsMap).sort((a, b) =>
      a.employee.localeCompare(b.employee)
    );
  }, [filteredRecords]);

  const formatDateRange = useCallback(() => {
    return formatCstDateRangeLabel(dateRange.start, dateRange.end);
  }, [dateRange]);

  const formatCurrency = useCallback((value) => {
    return parseFloat(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }, []);

  const handleDateRangeApply = (startDate, endDate) => {
    setDateRange({ start: startDate, end: endDate });
    setIsDatePickerOpen(false);
    setCurrentPage(1);
  };

  // PDF Export
  const exportToPDF = () => {
    try {
      const doc = new jsPDF();

      doc.setProperties({
        title: `${positionTitle} Tips Report`,
        subject: `${positionTitle} Tips Transactions`,
        author: "Elite Poker Lounge",
        creator: `${positionTitle} Tips System`
      });

      if (typeof base64Logo !== "undefined") {
        const logoWidth = 40;
        const logoHeight = 20;
        doc.addImage(base64Logo, "PNG", 150, 10, logoWidth, logoHeight);
      }

      const now = getCstNow().toDate();
      const dateTime = now.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
      });

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date & Time: ${dateTime}`, 14, 15);
      doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

      // Title
      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.setFont(undefined, "bold");
      doc.text(`${positionTitle} Tips Records`, 14, 40);

      // Balance Summary
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Balance Summary:", 14, 50);

      doc.setFont(undefined, "normal");
      doc.text(`Tip Pool: $${formatCurrency(summaryValues.tipPool)}`, 14, 60);
      doc.text(`Paid Out: $${formatCurrency(summaryValues.collectedFees)}`, 14, 70);
      doc.text(`Tax Collections: $${formatCurrency(summaryValues.addedTokes)}`, 14, 80);

      // Employee Totals
      doc.setFont(undefined, "bold");
      doc.text("Employee Totals:", 14, 95);

      const employeeHeaders = ["Employee", "Tips Received", "Tax", "Paid Out"];
      const employeeData = employeeTotals.map((emp) => [
        emp.employee,
        `$${formatCurrency(emp.tipsReceived)}`,
        `$${formatCurrency(emp.tax)}`,
        `$${formatCurrency(emp.tipsReceived - emp.tax)}`
      ]);

      employeeData.push([
        "TOTAL",
        `$${formatCurrency(
          employeeTotals.reduce((sum, emp) => sum + emp.tipsReceived, 0)
        )}`,
        `$${formatCurrency(
          employeeTotals.reduce((sum, emp) => sum + emp.tax, 0)
        )}`,
        `$${formatCurrency(
          employeeTotals.reduce((sum, emp) => sum + (emp.tipsReceived - emp.tax), 0)
        )}`
      ]);

      let currentY = 100;
      if (employeeTotals.length > 0) {
        autoTable(doc, {
          head: [employeeHeaders],
          body: employeeData,
          startY: currentY,
          theme: "plain",
          styles: {
            fontSize: 9,
            cellPadding: 3,
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.1
          },
          headStyles: {
            fillColor: false,
            textColor: [0, 0, 0],
            fontStyle: "bold",
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
          }
        });
        currentY = doc.lastAutoTable.finalY + 15;
      }

      // Transactions Section
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Transaction Details:", 14, currentY);

      const showOweToToast = positionSupportsOweToToast(positionTitle);
      const isOther =
        String(positionTitle || "").trim().toLowerCase().includes("other");

      const txHeaders = ["Date", "Employee"];
      if (isOther) {
        txHeaders.push("Position");
      }
      txHeaders.push("Tips Received", "Tax", "Paid Out");
      if (showOweToToast) {
        txHeaders.push("Owe To Toast");
      }
      txHeaders.push("Added By");

      const txData = filteredRecords.map((r) => {
        const emp = allEmployees.find((e) => e.name === r.employee);
        const empPos = (r.position && r.position.toLowerCase() !== "other")
          ? r.position
          : (emp?.role || r.role || positionTitle);

        const row = [
          moment(r.date).format("MM/DD/YYYY hh:mm A"),
          r.employee
        ];
        if (isOther) {
          row.push(empPos);
        }
        row.push(
          `$${formatCurrency(r.tip_out_cash)}`,
          `$${formatCurrency(r.tax || 0)}`,
          `$${formatCurrency(
            (parseFloat(r.tip_out_cash) || 0) - (parseFloat(r.tax) || 0)
          )}`
        );
        if (showOweToToast) {
          row.push(`$${formatCurrency(r.owe_to_toast || 0)}`);
        }
        row.push(r.added_by || "-");
        return row;
      });

      autoTable(doc, {
        head: [txHeaders],
        body: txData,
        startY: currentY + 5,
        theme: "plain",
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: "bold",
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        }
      });

      doc.save(`${positionTitle}_Tips_Report_${moment().format("YYYY-MM-DD")}.pdf`);
      toast.success("PDF exported successfully");
    } catch (err) {
      console.error("PDF Export error:", err);
      toast.error("Failed to export PDF");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          {positionTitle} Tips Records
        </h1>
        <p className="text-gray-600 mt-2">
          Manage and track {positionTitle.toLowerCase()} tip distributions
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
              Tip Pool
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-center">
              {isLoading ? (
                <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
              ) : (
                <div className="text-2xl font-bold text-blue-600">
                  ${formatCurrency(summaryValues.tipPool)}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
              Added Tokes
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-center">
              {isLoading ? (
                <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
              ) : (
                <div className="text-2xl font-bold text-red-600">
                  ${formatCurrency(summaryValues.addedTokes)}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
              Paid Out
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-center">
              {isLoading ? (
                <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
              ) : (
                <div className="text-2xl font-bold text-green-600">
                  ${formatCurrency(summaryValues.collectedFees)}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inline Quick-Add Entry Bar */}
      <InlineTipAddBar
        key={positionTitle || position}
        positionName={positionTitle}
        employees={allEmployees}
        onAdd={handleAddRecord}
        showOweToToast={positionSupportsOweToToast(positionTitle || position)}
      />

      {/* Filter Controls */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <Button
              variant="outline"
              onClick={() => setIsDatePickerOpen(true)}
              className="w-full justify-start text-left font-normal"
            >
              <Calendar className="mr-2 h-4 w-4" />
              {formatDateRange()}
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search records..."
            />
          </div>
        </div>
      </div>

      {/* Transactions Table Card */}
      <Card className="bg-white shadow rounded-lg mb-8">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold text-gray-800">
              Transaction History ({filteredRecords.length} records)
            </CardTitle>
            <Button
              onClick={exportToPDF}
              variant="outline"
              className="bg-yellow-400 hover:bg-yellow-500 text-black"
            >
              Export to PDF
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <PositionTipsTransactionTable
            records={filteredRecords}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            entriesPerPage={entriesPerPage}
            setEntriesPerPage={setEntriesPerPage}
            onEdit={(record) => {
              setCurrentRecord(record);
              setShowEditModal(true);
            }}
            onDelete={(record) => {
              setCurrentRecord(record);
              setShowDeleteModal(true);
            }}
            currentUser={currentUser}
            dateRange={dateRange}
            employeeApiService={employeeManagementService}
            employees={allEmployees}
            positionName={positionTitle}
          />
        </CardContent>
      </Card>

      {/* Overall Total Tips Received Card */}
      <Card className="bg-white shadow rounded-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-800">
            Overall Total Tips Received ({formatDateRange()})
          </CardTitle>
          <Button
            onClick={() => {
              fetchBalanceData(dateRange.start, dateRange.end);
              fetchRecords(dateRange.start, dateRange.end);
              fetchEditLogs();
            }}
            variant="outline"
            size="sm"
            disabled={isLoading}
          >
            {isLoading ? "Refreshing..." : "Refresh"}
          </Button>
        </CardHeader>
        <CardContent>
          <PositionEmployeeTotalsTable
            filteredRecords={filteredRecords}
            dateRange={dateRange}
            employeeApiService={employeeManagementService}
            employees={allEmployees}
            positionName={positionTitle}
          />
        </CardContent>
      </Card>

      {/* Record Change Activity Log - Bottom Most Position (After Overall Table) */}
      {canViewEditLogs && (
        <div className="mb-8">
          <TipEditLogsPanel
            moduleLabel={positionTitle}
            logs={editLogs}
            isLoading={editLogsLoading}
            canClear={canClearEditLogs}
            onClear={handleClearEditLogs}
            isClearing={editLogsClearing}
          />
        </div>
      )}

      {/* Modals */}

      {currentRecord && (
        <>
          <PositionTipEditModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setCurrentRecord(null);
            }}
            record={currentRecord}
            onUpdate={handleUpdateRecord}
            employees={allEmployees}
            positionName={positionTitle}
            currentUser={currentUser}
          />

          <PositionTipDeleteModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setCurrentRecord(null);
            }}
            record={currentRecord}
            onDelete={handleDeleteRecord}
            positionName={positionTitle}
          />
        </>
      )}

      {/* Date Range Picker */}
      <DateRangePicker
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        onApply={handleDateRangeApply}
        initialStartDate={dateRange.start}
        initialEndDate={dateRange.end}
        initialSelectedRange="thisMonth"
      />
    </div>
  );
}
